import React, { useCallback, useEffect, useState } from 'react';
import type {
  AudiFootnoteReferenceServiceInterfaceV3,
  EnumerableFootnote,
} from '@oneaudi/footnote-reference-service';
import {
  GLOBAL_COLOR_CANVAS_DEFAULT_DARK,
  GLOBAL_COLOR_CANVAS_DEFAULT_LIGHT,
  SYS_COLOR_FOREGROUND_NEUTRAL_200_DARK,
  SYS_COLOR_FOREGROUND_NEUTRAL_300_DARK,
  SYS_COLOR_FOREGROUND_NEUTRAL_400_DARK,
  SYS_COLOR_FOREGROUND_NEUTRAL_500_DARK,
  SYS_COLOR_FOREGROUND_NEUTRAL_600_DARK,
  SYS_SPACE_FIXED_300,
  SYS_SPACE_FIXED_500,
} from '@oneaudi/unified-web-common';
import {
  SYS_BORDER_RADIUS_MD,
  SYS_BREAKPOINT_XL,
  SYS_COLOR_CANVAS_100_DARK,
  SYS_COLOR_CANVAS_100_LIGHT,
} from '@oneaudi/unified-web-alpha-common';
import { Text, Layout, Divider } from '@oneaudi/unified-web-components';
import type { AnyStyledComponent } from 'styled-components';
import styled from 'styled-components';
import type { LayerManagerV27 } from '@volkswagen-onehub/layer-manager';
import { FocusLayerSizeV2 } from '@volkswagen-onehub/layer-manager';
import { LayerContentHTML } from '@oneaudi/fa-one-layer/dist/utils/cjs/LayerContentHTML';
import type { LocaleServiceV1 } from '@volkswagen-onehub/locale-service';
import type { Logger } from '@feature-hub/logger';
import { useTracking } from '../../utils/tracking';
import type { AudiFooterResponse } from '../../types/audi-footer-response.types';
import Categories from '../categories/categories';
import LanguageSwitch from '../language-switch/language-switch';
import LegalLinks from '../legal-links/legal-links';
import SocialMedia from '../social-media/social-media';
import ToTopLink from '../to-top-link/to-top-link';
import { useFootnoteReferenceServiceTextParserHook } from '../../services/hooks';
import { TrackingContext } from '../../context';
import { breakpoints } from '../../utils/breakpoints';
import { responsiveStyles } from '../../utils/responsiveStyles';
import { useContent } from '../../context/ContentContext';
import { getTypographyToken } from '../../../alpha-helper';

export type AudiFooterProps = {
  localeService?: LocaleServiceV1;
  countryCode: string;
  featureAppId?: string;
  referenceServiceManager?: AudiFootnoteReferenceServiceInterfaceV3;
  layerManager?: LayerManagerV27;
  logger?: Logger;
};

export const breakpointM = breakpoints.m;

const AudiFooter: React.FC<AudiFooterProps> = ({
  localeService,
  countryCode,
  featureAppId,
  referenceServiceManager,
  layerManager,
  logger,
}) => {
  const footerContent: AudiFooterResponse = useContent();
  const isHeadlessCms = true;
  const componentError = false;
  const useOneLayer = true;
  logger?.warn('footerContent', footerContent);
  const [footnotes, setFootnotes] = useState([]);
  const ref = React.useRef(null);

  const trackingService = React.useContext(TrackingContext);
  const { sendReadyEvent, registerImpressionTracking } = useTracking(trackingService);
  const enableMinimalFooter = footerContent?.enableMiniFooter ?? false;

  if (typeof window !== 'undefined') {
    // skip tracking on the server side where window object is undefined
    // eslint-disable-next-line react-hooks/rules-of-hooks
    React.useEffect(() => {
      sendReadyEvent();
    }, []);
    // eslint-disable-next-line react-hooks/rules-of-hooks
    React.useEffect(() => {
      requestAnimationFrame(() => {
        if (ref.current) registerImpressionTracking(ref);
      });
    }, []);
  }

  const disclaimerText = useFootnoteReferenceServiceTextParserHook({
    footnotes,
    htmlString: footerContent && footerContent.Disclaimer ? footerContent.Disclaimer : '',
    referenceServiceManager,
  });

  useEffect(() => {
    if (referenceServiceManager) {
      referenceServiceManager.registerCallback((_footnotes: EnumerableFootnote[]) => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        setFootnotes(_footnotes);
      });
    }

    return (): void => {
      if (referenceServiceManager) {
        referenceServiceManager.removeFootnoteReferences();
      }
    };
  }, [referenceServiceManager]);

  const handleLayerClick = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>): void => {
      const target = event.currentTarget as HTMLAnchorElement;
      const url = new URL(target.href).pathname;
      if (ref && isHeadlessCms) {
        event.preventDefault();
        layerManager?.openFocusLayer(
          () => <LayerContentHTML enableDeepLinking url={url} />,
          {},
          {
            size: FocusLayerSizeV2.A,
            userCloseable: true,
          },
        );
      } else {
        console.warn('[oneaudi-feature-app-nemo-footer]: Please configure a valid layer link');
      }
    },
    [layerManager],
  );

  if (componentError) {
    return <div>Error</div>;
  }

  if (footerContent === undefined) {
    return <div>Loading...</div>;
  }

  const filteredLegalLinks = footerContent.LegalLinks || [];

  return (
    <OneFooterStyled
      ref={ref}
      data-testid="footer"
      data-fefa-custom-id={featureAppId}
      data-module="one-footer"
    >
      {enableMinimalFooter ? (
        <>
          {footerContent?.LegalLinks && (
            <OneFooterStyledMinimalFooterContainer>
              <TextNoCursor
                className="copyrightText"
                variant={{
                  type: 'copy',
                  order: '2',
                  style: 'normal',
                }}
              >
                {footerContent?.Copyright}
              </TextNoCursor>
              {footerContent?.LegalLinks && (
                <LegalLinks
                  countryCode={countryCode}
                  links={filteredLegalLinks}
                  useOneLayer={useOneLayer}
                  openLayer={handleLayerClick}
                />
              )}
              {footerContent?.LanguageLinks && footerContent?.LanguageLinks.length > 0 && (
                <LanguageSwitch links={footerContent?.LanguageLinks} />
              )}
            </OneFooterStyledMinimalFooterContainer>
          )}
        </>
      ) : (
        <>
          <OneFooterStyledToolbar>
            <ToTopLink label={footerContent?.ToTopLabel} />
          </OneFooterStyledToolbar>
          <>
            <Categories
              categories={footerContent?.Categories}
              useOneLayer={useOneLayer}
              openLayer={handleLayerClick}
            />
            {footerContent?.SocialMedia && footerContent?.SocialMedia.length > 0 && (
              <SocialMedia socialMedia={footerContent?.SocialMedia} />
            )}
            <Divider orientation="horizontal" />
            <OneFooterStyledToolbarBorderTop>
              <TextNoCursor
                className="copyrightText"
                variant={{
                  type: 'copy',
                  order: '2',
                  style: 'normal',
                }}
              >
                {footerContent?.Copyright}
              </TextNoCursor>

              {footerContent?.LegalLinks && (
                <LegalLinks
                  countryCode={countryCode}
                  links={footerContent?.LegalLinks}
                  useOneLayer={useOneLayer}
                  openLayer={handleLayerClick}
                />
              )}
              {footerContent?.LanguageLinks && footerContent?.LanguageLinks.length > 0 && (
                <LanguageSwitch links={footerContent?.LanguageLinks} />
              )}
            </OneFooterStyledToolbarBorderTop>
            {footerContent && footerContent?.Disclaimer && footerContent?.Disclaimer.length > 0 && (
              <Text
                tag="p"
                className="disclaimer-text"
                variant={{
                  type: 'copy',
                  order: '2',
                  style: 'normal',
                }}
              >
                <span
                  id="disclaimer-text"
                  dangerouslySetInnerHTML={{
                    __html: disclaimerText.replace(
                      /<script\b[^<]*(?:(?!<\/script\s*>)[\s\S])*<\/script[^>]*>/gi,
                      'illegalscript',
                    ),
                  }}
                />
              </Text>
            )}
            {/* logo image and image text limited only for the Brazilian market now */}
            {/* remove the localeService conditon if other markets reqiires */}
            {(countryCode === 'BR' && footerContent?.ImageUrl) ||
            (localeService?.countryCode.toLocaleLowerCase() === 'br' && footerContent?.ImageUrl) ||
            (isHeadlessCms && footerContent?.ImageUrl) ? (
              <Layout className="layout" align="center" justify="flex-start">
                <LogoImage src={footerContent.ImageUrl} alt={footerContent?.ImageText} />
                {footerContent?.FooterImageAriaLabel && (
                  <Text
                    variant={{
                      type: 'copy',
                      order: '2',
                      style: 'normal',
                    }}
                  >
                    {footerContent.FooterImageAriaLabel}
                  </Text>
                )}
              </Layout>
            ) : null}
          </>
        </>
      )}
    </OneFooterStyled>
  );
};

const OneFooterStyled = styled.div<{
  children: React.ReactNode;
}>`
  --one-footer-color-black: ${process.env.IS_LEGACY
    ? GLOBAL_COLOR_CANVAS_DEFAULT_DARK
    : SYS_COLOR_CANVAS_100_DARK};
  --one-footer-color-white: ${process.env.IS_LEGACY
    ? GLOBAL_COLOR_CANVAS_DEFAULT_LIGHT
    : SYS_COLOR_CANVAS_100_LIGHT};
  --one-footer-neutral-5: ${SYS_COLOR_FOREGROUND_NEUTRAL_600_DARK};
  --one-footer-neutral-10: ${SYS_COLOR_FOREGROUND_NEUTRAL_500_DARK};
  --one-footer-neutral-20: ${SYS_COLOR_FOREGROUND_NEUTRAL_400_DARK};
  --one-footer-neutral-70: ${SYS_COLOR_FOREGROUND_NEUTRAL_300_DARK};
  --one-footer-side-spacing: 16px;
  --one-footer-space-xs: var(--spacing-relative-xs);
  --one-footer-space-s: var(--spacing-relative-sm);
  --one-footer-space-m: var(--spacing-relative-md);
  --one-footer-space-l: var(--spacing-relative-lg);
  --one-footer-space-xl: var(--spacing-relative-xl);
  --one-footer-space-xxl: var(--spacing-relative-2xl);
  --one-footer-space-xxxl: var(--spacing-relative-3xl);
  background: var(--one-footer-color-black);
  box-sizing: border-box;
  color: var(--one-footer-color-white);
  .layout {
    margin-block-start: var(--spacing-relative-lg);
    column-gap: var(--spacing-relative-sm);
  }
  .layout p {
    display: inline;
  }
  .disclaimer-text {
    margin-block-start: var(--spacing-relative-lg);
  }
  span#disclaimer-text > a {
    color: inherit;
    text-decoration: none !important;
    font-stretch: ${getTypographyToken().fontStretch};
    font-family: ${getTypographyToken().fontFamily};
    font-weight: ${getTypographyToken().fontWeight};
    transition: 250ms cubic-bezier(0.75, 0.02, 0.5, 1);
    border-bottom: 1px solid var(--one-footer-color-white);
    cursor: pointer !important;
    outline: none;
  }

  span#disclaimer-text > a:focus-visible {
    outline: revert;
  }

  .copyrightText {
    color: ${SYS_COLOR_FOREGROUND_NEUTRAL_200_DARK};
    margin-inline-end: var(--one-footer-space-xxl);
  }
  ${(): string =>
    responsiveStyles(
      {
        '--one-footer-side-spacing': {
          l: '60px',
          m: '40px',
          s: '28px',
          xl: '96px',
          xxl: 'calc((100% - 1728px) / 2)',
        },
      },
      breakpoints,
    )}
  /* stylelint-disable */
    padding: var(--one-footer-space-l) var(--one-footer-side-spacing) var(--one-footer-space-xl);
  /* stylelint-enable */

  & *,
  & *::after,
  & *::before {
    box-sizing: border-box;
  }

  & a,
  & select,
  & span {
    /* stylelint-disable */
    cursor: pointer;
    /* stylelint-enable */
    text-decoration: none;
  }

  & a:hover,
  & select:hover,
  & a:hover span {
    color: var(--one-footer-neutral-70);
  }

  & a:focus-visible,
  & select:focus-visible {
    outline: var(--one-footer-neutral-20) auto 1px;
  }
  & [data-is-overview-link='true'] {
    display: none !important;
  }
`;

const OneFooterStyledToolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
`;

const OneFooterStyledToolbarBorderTop = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  margin: ${SYS_SPACE_FIXED_300} 0 0;
  ${(): string =>
    responsiveStyles(
      {
        margin: { m: `${SYS_SPACE_FIXED_500} 0 0` },
      },
      breakpoints,
    )}
`;

const OneFooterStyledMinimalFooterContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: left;
  flex-direction: column;
  @media (min-width: ${SYS_BREAKPOINT_XL}) {
    flex-direction: row;
    align-items: center;
  }
`;

// add display: inline instead span tag to avoid pointer's cursor
const TextNoCursor = styled(Text as AnyStyledComponent)`
  color: var(--one-footer-color-white);
  display: inline;
`;

const LogoImage = styled.img<{ src: string; alt?: string }>`
  max-width: 48px;
  max-height: 48px;
  border-radius: ${process.env.IS_LEGACY ? 'none' : SYS_BORDER_RADIUS_MD};
`;
export default AudiFooter;
